import React, { useCallback, useMemo, useState } from "react";
import { AppBar, Toolbar, IconButton, Typography, Box, CircularProgress, Alert, Chip } from "@mui/material";
import { Logout } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import usePaginatedList from "../hooks/usePaginatedList";
import OrderCard from "../components/OrderCard";
import LoadMoreButton from "../components/LoadMoreButton";

// Today plus the two days before it — matches the backend's own 3-day cap on
// st=delivery_completed (OrderController@getOrders), so this never offers a
// day the API would reject/ignore.
function lastThreeDays() {
  const days = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    days.push({
      iso,
      label: i === 0 ? "Today" : i === 1 ? "Yesterday" : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
    });
  }
  return days;
}

export default function DeliveryHistory() {
  const { store, logout } = useAuth();
  const navigate = useNavigate();
  const currency = store?.currency || store?.price_type || "";
  const days = useMemo(lastThreeDays, []);
  const [selectedDate, setSelectedDate] = useState(days[0].iso);

  // GET pos/v1/orders/get-orders?st=delivery_completed&date=YYYY-MM-DD&page=N
  // — this driver's own completed deliveries for the selected day, newest
  // first (server-side delivery_by=Auth::id(), ordered by updated_at desc).
  const fetchPage = useCallback(
    (page) =>
      apiClient.get("orders/get-orders", { params: { st: "delivery_completed", date: selectedDate, page } }).then((res) => {
        const paginator = res.data?.orders || {};
        return { items: paginator.data || [], hasMore: (paginator.current_page || 1) < (paginator.last_page || 1) };
      }),
    [selectedDate]
  );
  const { items: orders, loading, loadingMore, hasMore, error, loadMore } = usePaginatedList(fetchPage, [selectedDate]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <Box>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar>
          <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
            Delivery History
          </Typography>
          <IconButton onClick={handleLogout} size="small">
            <Logout fontSize="small" />
          </IconButton>
        </Toolbar>
        <Box sx={{ px: 2, pb: 1.5, display: "flex", gap: 1 }}>
          {days.map((d) => (
            <Chip
              key={d.iso}
              label={d.label}
              clickable
              color={selectedDate === d.iso ? "primary" : "default"}
              onClick={() => setSelectedDate(d.iso)}
            />
          ))}
        </Box>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            No deliveries completed on this day.
          </Typography>
        ) : (
          <>
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} currency={currency} />
            ))}
            <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
          </>
        )}
      </Box>
    </Box>
  );
}
